/**
 * PublicSvmView.jsx - the customer-facing SVM sign-off sheet.
 *
 * Rendered STANDALONE from a share link (#/svm-view/<token>), before the auth
 * gate: no sidebar, no login. The token in the URL is the credential and is
 * validated server-side by /api/svm-public.
 *
 * The page's pieces live in ./publicSvm/ - theme tokens, pure helpers, and one
 * file per band of the sheet.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { TitleBlock } from './publicSvm/TitleBlock';
import { SummaryBand } from './publicSvm/SummaryBand';
import { DisciplineIndex } from './publicSvm/DisciplineIndex';
import { DisciplineSection } from './publicSvm/DisciplineSection';
import { ErrorBand, Notice } from './publicSvm/Notices';
import { Shell } from './publicSvm/Shell';

export default function PublicSvmView({ token }) {
  const [data, setData] = useState(null);
  const [gone, setGone] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [loadedAt, setLoadedAt] = useState(null);
  const [now, setNow] = useState(Date.now());
  const goneRef = useRef(false);
  const busyRef = useRef(false);

  const load = useCallback(async () => {
    if (!token || goneRef.current || busyRef.current) return;
    busyRef.current = true;
    try {
      const res = await fetch('/api/svm-public/' + encodeURIComponent(token));
      if (res.status === 404 || res.status === 410) {
        goneRef.current = true;
        setGone(true);
        return;
      }
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const body = await res.json();
      setData(body);
      setFetchError(false);
      setLoadedAt(Date.now());
    } catch (err) {
      setFetchError(true);
    } finally {
      busyRef.current = false;
      setNow(Date.now()); // relative "Refreshed ..." updates with every poll
    }
  }, [token]);

  useEffect(() => {
    if (!token) return undefined;
    load();
    const interval = setInterval(load, 30000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [token, load]);

  // "Refreshed X ago" keeps ticking between polls.
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(tick);
  }, []);

  if (!token || gone) {
    return (
      <Shell>
        <TitleBlock status="inactive" />
        <Notice label="Notice">This link is no longer active. Ask your GLOBAL contact for a new one.</Notice>
      </Shell>
    );
  }

  if (!data) {
    return (
      <Shell>
        <TitleBlock />
        {fetchError && <ErrorBand onRetry={load} />}
        <Notice>Loading verification status...</Notice>
      </Shell>
    );
  }

  const disciplines = data.disciplines || [];

  return (
    <Shell>
      <TitleBlock project={data.project} status="live" generatedAt={data.generatedAt} loadedAt={loadedAt} now={now} />

      {fetchError && <ErrorBand onRetry={load} />}

      <SummaryBand disciplines={disciplines} />

      {disciplines.length === 0 ? (
        <Notice label="Status">
          Verification has not started on this project yet. Sign-off records appear on this report as GLOBAL engineers complete system
          checks.
        </Notice>
      ) : (
        <DisciplineIndex disciplines={disciplines} />
      )}

      {disciplines.map((discipline, i) => (
        <DisciplineSection key={discipline.id || i} discipline={discipline} index={i} />
      ))}
    </Shell>
  );
}
